"use client";

import { useState, FormEvent } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import { ShoppingCart, UserRound, Menu, Search, Heart } from "lucide-react";
import HeartSolid from "@/components/icons/heart-solid.svg";
//import Heart from "@/components/icons/heart.svg";

import SideMenu from "./SideMenu";
import Image from "next/image";
import useOutsideClick from "./ui/useOutsideClick";
import { useAppSelector } from "@/lib/client/storeHooks";
import { formatMoney } from "@/lib/money-utils";

export default function Navigation() {
  //const storeDispatch = useAppDispatch();
  const currentSessionInfo = useAppSelector(state => state.session);
  const currentCart = useAppSelector(state => state.cart);
  const currentArticleCount = useAppSelector(state => (state.cart.articles || []).reduce((acc, val) => acc + val.qty, 0) || 0);
  const currentCartAmount = useAppSelector(state => state.cart.grossTotal || 0);
  const currentWishlist = useAppSelector(state => state.wishlist.list);
  const [currentMenuOpen, setCurrentMenuOpen] = useState("");
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("Menu");

  const pathname = usePathname();

  const toggleUserMenu = () => {
    if (currentMenuOpen === "user") {
      setCurrentMenuOpen("");
    } else {
      setCurrentMenuOpen("user");
    }
  }

  const toggleMainMenu = () => {
    if (currentMenuOpen === "main") {
      setCurrentMenuOpen("");
    } else {
      setCurrentMenuOpen("main");
    }
  }

  const closeMenu = () => {
    setCurrentMenuOpen("");
  }

  const menuOutsideClickRef = useOutsideClick<HTMLDivElement>(closeMenu, [currentMenuOpen]);

  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const query = formData.get("search") as string;

    router.replace(`/suche?q=${encodeURIComponent(query)}`);
  };

  //console.log('Current cart: ', currentCart);

  return (
    <>
      <div className="bg-primary border-b-[2px] border-accent sticky top-0 z-50">
        <div className="navbar hidden md:flex md:flex-row mx-auto max-w-[1920px] flex-col items-center justify-between px-2 py-2 pl-4 text-white gap-2 md:gap-14 h-[75px]">
          <div className="">
            <Link href="/">
              <svg className='w-[150px]' viewBox="0 0 736 135" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_348_2070)">
                  <path d="M32.25 28.71H0V129H32.25V28.71Z" fill="white" />
                  <path d="M110.52 23.55C82.12 23.55 59.09 46.5801 59.09 74.9801V129.01H91.34V74.9801C91.34 64.3901 99.93 55.8 110.52 55.8C121.11 55.8 129.69 64.3901 129.69 74.9801V129.01H161.94V74.9801C161.94 46.5801 138.92 23.55 110.52 23.55Z" fill="white" />
                  <path d="M385.39 43.41C380.9 37.87 375.21 33.55 368.33 30.43C361.45 27.32 353.61 25.76 344.83 25.76C336.83 25.76 329.51 27.05 322.85 29.64C316.2 32.23 310.41 35.89 305.49 40.61C300.57 45.33 296.77 50.94 294.08 57.43C291.39 63.92 290.05 71.03 290.05 78.77C290.05 89.46 292.36 98.78 296.98 106.74C298.31 109.03 299.81 111.15 301.44 113.14C338.98 155.17 389.46 121.27 389.46 121.27L378.54 97.74C373.69 100.95 368.61 103.33 363.3 104.87C357.99 106.41 352.38 107.18 346.48 107.18C341.17 107.18 336.51 106.34 332.52 104.67C328.52 103 325.37 100.69 323.08 97.74C320.79 94.79 319.61 91.35 319.54 87.42H394.86C396.43 78.63 396.37 70.49 394.66 62.98C392.96 55.48 389.86 48.96 385.37 43.41H385.39ZM321.52 65.68C322.04 60.63 324.35 56.6 328.45 53.58C332.55 50.57 337.71 49.06 343.94 49.06C350.17 49.06 354.99 50.57 358.59 53.58C362.2 56.6 364.06 60.63 364.2 65.68H321.52Z" fill="white" />
                  <path d="M230.16 95.1L210.61 28.71H176L210.8 127.3C230.09 127.7 228.67 127.64 252.69 127.3L283.08 28.71H249.26L230.16 95.1Z" fill="white" />
                  <path d="M728.39 52.31C723.67 44.25 717.11 37.81 708.72 32.99C700.33 28.17 690.66 25.76 679.71 25.76C671.51 25.76 664.01 27.19 657.19 30.04C650.37 32.89 644.47 36.79 639.49 41.74C634.51 46.69 630.66 52.36 627.94 58.75C625.22 65.14 623.86 71.84 623.86 78.86C623.86 88.49 626.24 97.34 630.99 105.41C635.74 113.47 642.31 119.91 650.7 124.73C659.09 129.55 668.76 131.96 679.71 131.96C687.91 131.96 695.41 130.52 702.23 127.63C709.05 124.75 714.94 120.85 719.93 115.93C724.91 111.01 728.75 105.36 731.44 98.97C734.13 92.58 735.47 85.88 735.47 78.86C735.47 69.22 733.11 60.37 728.39 52.31ZM700.76 92.33C698.66 96.13 695.78 99.07 692.11 101.13C688.44 103.19 684.31 104.23 679.72 104.23C675.13 104.23 671 103.2 667.33 101.13C663.66 99.06 660.76 96.13 658.63 92.33C656.5 88.53 655.43 84.04 655.43 78.86C655.43 73.68 656.48 69.19 658.58 65.39C660.68 61.59 663.56 58.65 667.23 56.59C670.9 54.52 675.06 53.49 679.72 53.49C684.38 53.49 688.54 54.52 692.21 56.59C695.88 58.66 698.75 61.59 700.81 65.39C702.87 69.19 703.91 73.68 703.91 78.86C703.91 84.04 702.86 88.53 700.76 92.33Z" fill="white" />
                  <path d="M602.88 102.7C600.88 103.19 598.86 103.44 596.83 103.44C593.09 103.44 590.04 102.36 587.68 100.19C585.32 98.03 584.14 94.26 584.14 88.88V54.26H607.64V28.69H584.14V0H551.89V28.71H532.42V54.28H551.89V94.5C551.89 106.96 555.07 116.31 561.43 122.57C567.79 128.83 576.54 131.96 587.69 131.96C592.54 131.96 597.13 131.35 601.46 130.14C605.79 128.93 609.49 127.47 612.57 125.76L608.44 100.49C606.74 101.47 604.88 102.21 602.88 102.7Z" fill="white" />
                  <path d="M466.91 23.27C438.51 23.27 415.48 46.3 415.48 74.7V128.73H447.73V74.7C447.73 64.11 456.32 55.52 466.91 55.52C477.5 55.52 486.08 64.11 486.08 74.7V128.73H518.33V74.7C518.33 46.3 495.31 23.27 466.91 23.27Z" fill="white" />
                </g>
                <defs>
                  <clipPath id="clip0_348_2070">
                    <rect width="735.47" height="134.18" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </Link>
          </div>

          <div className="flex h-5 items-center gap-[8px] flex-row place-items-center">
            <div className='bg-white/[14%] p-1 pl-[16px] rounded-full w-full flex h-[40px]'>
              <div className='flex items-center gap-3 w-90 md:w-90'>
                <Search />
                <form onSubmit={handleSearch} className="w-full h-full">
                  <input name="search" placeholder={t("search")} className='input input-md input-ghost w-full bg-transparent outline-none h-full text-white' />
                </form>
                <Link href={"/kategorie"}><button className="btn btn-white text-primary btn-sm rounded-full">{t('all-products')}</button></Link>
              </div>
            </div>
            <div className="gap-[16px] w-auto flex flex-row items-center place-items-center shrink-0">
              <Link href="/warenkorb" className="py-3 px-2 shrink-0 font-[Outfit] font-normal">{formatMoney(currentCartAmount)}</Link>
              <Link href="/warenkorb" className="py-3 px-2 w-10 relative">
                <ShoppingCart size={24} />
                {currentArticleCount !== 0 && (
                  <div className="badge badge-secondary badge-sm rounded-full outline-white outline-1 absolute top-0.5 left-4.5">{currentArticleCount}</div>
                )}
              </Link>
              {currentWishlist.length == 0 ? (
                <Link href="/wunschliste" className="py-3 px-2"><Heart size={24} color="white" /></Link>
              ) : (
                <Link href="/wunschliste" className="py-3 px-2"><HeartSolid height={24} width={24} className="fill-secondary" /></Link>
              )}
              {(currentSessionInfo && currentSessionInfo.user) ? (
                <div className="py-3 px-2 flex flex-col">
                  <button className="px-1 hover:cursor-pointer w-6 h-6 rounded-full bg-white" onClick={toggleUserMenu}>
                    <Image className="" src="/invento-max-transparent.png" alt="invento max" height={19} width={17} />
                  </button>
                </div>
              ) : (
                <Link href={`/kundenkonto/login?continue=${encodeURIComponent(pathname)}`} className="py-3 px-2"><UserRound size={24} /></Link>
              )}
              <button className="py-3 px-2 hover:cursor-pointer" onClick={toggleMainMenu}><Menu size={24} /></button>
            </div>
          </div>
        </div>
        {/* ----/ Mobile Menu /---- */}
        <nav className="md:hidden mx-auto max-w-[1440px] p-4 grid grid-cols-1 text-white gap-2 relative z-50">
          <div className="flex justify-between items-center">
            <Link href="/">
              <svg className='w-[120px]' viewBox="0 0 736 135" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_348_2070)">
                  <path d="M32.25 28.71H0V129H32.25V28.71Z" fill="white" />
                  <path d="M110.52 23.55C82.12 23.55 59.09 46.5801 59.09 74.9801V129.01H91.34V74.9801C91.34 64.3901 99.93 55.8 110.52 55.8C121.11 55.8 129.69 64.3901 129.69 74.9801V129.01H161.94V74.9801C161.94 46.5801 138.92 23.55 110.52 23.55Z" fill="white" />
                  <path d="M385.39 43.41C380.9 37.87 375.21 33.55 368.33 30.43C361.45 27.32 353.61 25.76 344.83 25.76C336.83 25.76 329.51 27.05 322.85 29.64C316.2 32.23 310.41 35.89 305.49 40.61C300.57 45.33 296.77 50.94 294.08 57.43C291.39 63.92 290.05 71.03 290.05 78.77C290.05 89.46 292.36 98.78 296.98 106.74C298.31 109.03 299.81 111.15 301.44 113.14C338.98 155.17 389.46 121.27 389.46 121.27L378.54 97.74C373.69 100.95 368.61 103.33 363.3 104.87C357.99 106.41 352.38 107.18 346.48 107.18C341.17 107.18 336.51 106.34 332.52 104.67C328.52 103 325.37 100.69 323.08 97.74C320.79 94.79 319.61 91.35 319.54 87.42H394.86C396.43 78.63 396.37 70.49 394.66 62.98C392.96 55.48 389.86 48.96 385.37 43.41H385.39ZM321.52 65.68C322.04 60.63 324.35 56.6 328.45 53.58C332.55 50.57 337.71 49.06 343.94 49.06C350.17 49.06 354.99 50.57 358.59 53.58C362.2 56.6 364.06 60.63 364.2 65.68H321.52Z" fill="white" />
                  <path d="M230.16 95.1L210.61 28.71H176L210.8 127.3C230.09 127.7 228.67 127.64 252.69 127.3L283.08 28.71H249.26L230.16 95.1Z" fill="white" />
                  <path d="M728.39 52.31C723.67 44.25 717.11 37.81 708.72 32.99C700.33 28.17 690.66 25.76 679.71 25.76C671.51 25.76 664.01 27.19 657.19 30.04C650.37 32.89 644.47 36.79 639.49 41.74C634.51 46.69 630.66 52.36 627.94 58.75C625.22 65.14 623.86 71.84 623.86 78.86C623.86 88.49 626.24 97.34 630.99 105.41C635.74 113.47 642.31 119.91 650.7 124.73C659.09 129.55 668.76 131.96 679.71 131.96C687.91 131.96 695.41 130.52 702.23 127.63C709.05 124.75 714.94 120.85 719.93 115.93C724.91 111.01 728.75 105.36 731.44 98.97C734.13 92.58 735.47 85.88 735.47 78.86C735.47 69.22 733.11 60.37 728.39 52.31ZM700.76 92.33C698.66 96.13 695.78 99.07 692.11 101.13C688.44 103.19 684.31 104.23 679.72 104.23C675.13 104.23 671 103.2 667.33 101.13C663.66 99.06 660.76 96.13 658.63 92.33C656.5 88.53 655.43 84.04 655.43 78.86C655.43 73.68 656.48 69.19 658.58 65.39C660.68 61.59 663.56 58.65 667.23 56.59C670.9 54.52 675.06 53.49 679.72 53.49C684.38 53.49 688.54 54.52 692.21 56.59C695.88 58.66 698.75 61.59 700.81 65.39C702.87 69.19 703.91 73.68 703.91 78.86C703.91 84.04 702.86 88.53 700.76 92.33Z" fill="white" />
                  <path d="M602.88 102.7C600.88 103.19 598.86 103.44 596.83 103.44C593.09 103.44 590.04 102.36 587.68 100.19C585.32 98.03 584.14 94.26 584.14 88.88V54.26H607.64V28.69H584.14V0H551.89V28.71H532.42V54.28H551.89V94.5C551.89 106.96 555.07 116.31 561.43 122.57C567.79 128.83 576.54 131.96 587.69 131.96C592.54 131.96 597.13 131.35 601.46 130.14C605.79 128.93 609.49 127.47 612.57 125.76L608.44 100.49C606.74 101.47 604.88 102.21 602.88 102.7Z" fill="white" />
                  <path d="M466.91 23.27C438.51 23.27 415.48 46.3 415.48 74.7V128.73H447.73V74.7C447.73 64.11 456.32 55.52 466.91 55.52C477.5 55.52 486.08 64.11 486.08 74.7V128.73H518.33V74.7C518.33 46.3 495.31 23.27 466.91 23.27Z" fill="white" />
                </g>
                <defs>
                  <clipPath id="clip0_348_2070">
                    <rect width="735.47" height="134.18" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </Link>
            <div className="flex gap-2">
              <Link href="/warenkorb" className="py-3 px-2 w-10 relative">
                <ShoppingCart size={24} />
                {currentArticleCount !== 0 && (
                  <div className="badge badge-secondary badge-sm rounded-full outline-white outline-1 absolute top-0.5 left-4.5">{currentArticleCount}</div>
                )}
              </Link>
              {currentWishlist.length == 0 ? (
                <Link href="/wunschliste" className="py-3 px-2"><Heart size={24} color="white" /></Link>
              ) : (
                <Link href="/wunschliste" className="py-3 px-2"><HeartSolid height={24} width={24} className="fill-secondary" /></Link>
              )}
              {(currentSessionInfo && currentSessionInfo.user) ? (
                <div className="py-3 px-2 flex flex-col">
                  <button className="px-1 hover:cursor-pointer w-6 h-6 rounded-full bg-white" onClick={toggleUserMenu}>
                    <Image className="" src="/invento-max-transparent.png" alt="invento max" height={19} width={17} />
                  </button>
                </div>
              ) : (
                <Link href="/kundenkonto/login" className="py-3 px-2"><UserRound size={24} /></Link>
              )}
              <button className="py-3 px-2 hover:cursor-pointer" onClick={toggleMainMenu}><Menu size={24} /></button>
            </div>
          </div>
          <div className='bg-white/[14%] p-1 pl-[16px] rounded-full w-full flex h-[40px]'>
            <div className='flex items-center gap-3 w-90 md:w-90'>
              <Search />
              <form onSubmit={handleSearch} className="w-full h-full">
                <input name="search" placeholder={t("search")} className='input input-md input-ghost w-full bg-transparent outline-none h-full text-white' />
              </form>
              <Link href={"/kategorie"}><button className="btn btn-white text-primary btn-sm rounded-full">{t('all-products')}</button></Link>
            </div>
          </div>
        </nav>
      </div >
      {currentMenuOpen !== "" && (
        <SideMenu type={currentMenuOpen as "user" | "main"} open={true} close={closeMenu} divRef={menuOutsideClickRef} />
      )}
    </>
  );
}
